import React, { useContext } from 'react';
import type { TabsProps as RadixTabsProps, TabsListProps } from '@radix-ui/react-tabs';
import { Content, List, Root, Trigger } from '@radix-ui/react-tabs';
import { styled } from '@components-shared/src/stitches';
import { Indicator } from '../indicator';

export type TabsProps = RadixTabsProps & { size?: TabsContextType };

type TabsContextType = 'sm' | 'md' | 'lg' | 'xl';

export const TabsContext = React.createContext<TabsContextType | null>(null);

const StyledTabTrigger = styled(Trigger, {
  transition: 'color 0.1s ease',
  textTransform: 'capitalize',
  WebkitTapHighlightColor: 'transparent',
  borderBottom: '2px solid transparent',
  outline: 'none',
  paddingBottom: '4px',
  title: 'M',

  '@tablet': {
    paddingBottom: '8px',
  },

  '&[data-state="active"]': {
    color: '$base800',
    borderBottom: '2px solid $black',
  },
  '&[data-state="inactive"]': {
    color: '$base500',
  },

  '&:hover': {
    color: '$base800',
  },

  '&[data-disabled]': {
    cursor: 'not-allowed',
    borderBottom: 'none',
    color: '$base500',
    opacity: 0.3,
  },

  variants: {
    size: {
      xl: {
        fontSize: 32,
        lineHeight: '40px',
        fontFamily: '$title',
        fontWeight: 500,
      },
      lg: {
        title: 'L',
      },
      md: {
        title: 'M',
      },
      sm: {
        title: 'S',
      },
    },
    hasIcon: {
      true: {
        display: 'flex',
      },
    },
  },
});

export type TriggerProps = {
  label: string;
  showIndicator?: boolean;
  icon?: React.ReactNode;
} & React.ComponentProps<typeof StyledTabTrigger>;

export const TabTrigger = React.forwardRef<HTMLButtonElement, TriggerProps>(
  ({ label, icon, showIndicator, ...props }, forwardRef) => {
    const size = useContext(TabsContext);

    return (
      <StyledTabTrigger {...props} ref={forwardRef} size={size || undefined} hasIcon={!!icon}>
        {label}
        {icon}
        {!!showIndicator && <Indicator />}
      </StyledTabTrigger>
    );
  },
);

TabTrigger.displayName = 'TabTrigger';

export const StyledTabList = styled(List, {
  position: 'relative',
  display: 'flex',
  flexShrink: 0,
  width: '100%',
  gap: 24,
  verticalAlign: 'baseline',
  borderBottom: '1px solid $base200',

  variants: {
    isXL: {
      true: { gap: 32 },
    },
  },
});

export const TabList = ({ children, ...props }: TabsListProps) => {
  const size = useContext(TabsContext);

  return (
    <StyledTabList isXL={size === 'xl'} {...props}>
      {children}
    </StyledTabList>
  );
};

export const Tabs = ({ size, ...props }: TabsProps) => {
  return (
    <TabsContext.Provider value={size || null}>
      <Root {...props} />
    </TabsContext.Provider>
  );
};

export { Content as TabContent, List, Trigger };
